import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axiosConfig';
import { useAuth } from '../context/AuthContext';

const SuperAdminSessionManagement = () => {
  const { isAuthReady, isAuthenticated, isRefreshRevoked } = useAuth();
  const [activeTab, setActiveTab] = useState('sessions');
  const [sessions, setSessions] = useState([]);
  const [locks, setLocks] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [locksLoading, setLocksLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchSessions = useCallback(async () => {
    if (!isAuthReady || !isAuthenticated || isRefreshRevoked) {
      setSessions([]);
      return;
    }

    setSessionsLoading(true);
    try {
      const res = await api.get('/superadmin/sessions/active');
      setSessions(res.data.sessions || []);
      setLastUpdated(new Date());
    } catch (err) {
      showToast('Failed to load sessions', 'error');
    } finally {
      setSessionsLoading(false);
    }
  }, [isAuthReady, isAuthenticated, isRefreshRevoked]);

  const fetchLocks = useCallback(async () => {
    if (!isAuthReady || !isAuthenticated || isRefreshRevoked) {
      setLocks([]);
      return;
    }

    setLocksLoading(true);
    try {
      const res = await api.get('/superadmin/sessions/locks');
      setLocks(res.data.locks || []);
      setLastUpdated(new Date());
    } catch (err) {
      showToast('Failed to load locks', 'error');
    } finally {
      setLocksLoading(false);
    }
  }, [isAuthReady, isAuthenticated, isRefreshRevoked]);

  // Initial load
  useEffect(() => {
    if (!isAuthReady || !isAuthenticated || isRefreshRevoked) {
      return;
    }

    fetchSessions();
    fetchLocks();
  }, [fetchSessions, fetchLocks, isAuthReady, isAuthenticated, isRefreshRevoked]);

  // Auto-refresh locks every 30 seconds
  useEffect(() => {
    if (!isAuthReady || !isAuthenticated || isRefreshRevoked) {
      return;
    }

    const interval = setInterval(fetchLocks, 30000);
    return () => clearInterval(interval);
  }, [fetchLocks, isAuthReady, isAuthenticated, isRefreshRevoked]);

  const handleRevokeSession = async (sessionId, userName) => {
    if (!isAuthReady || !isAuthenticated || isRefreshRevoked) {
      return;
    }

    if (!window.confirm(
      `Force logout ${userName}? This will immediately end their session.`
    )) return;

    try {
      await api.delete(`/superadmin/sessions/${sessionId}/revoke`);
      setSessions(prev => prev.filter(s => s.sessionId !== sessionId));
      showToast(`Session for ${userName} revoked successfully`);
    } catch (err) {
      showToast('Failed to revoke session', 'error');
    }
  };

  const handleReleaseLock = async (lockId, lockedByName) => {
    if (!isAuthReady || !isAuthenticated || isRefreshRevoked) {
      return;
    }

    if (!window.confirm(
      `Force unlock this record? ${lockedByName} will lose their lock.`
    )) return;

    try {
      await api.delete(`/superadmin/locks/${lockId}/release`);
      setLocks(prev => prev.filter(l => l.lockId !== lockId));
      showToast('Lock released successfully');
    } catch (err) {
      showToast('Failed to release lock', 'error');
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  const formatRelative = (dateStr) => {
    if (!dateStr) return '—';
    const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000 / 60);
    if (diff < 1) return 'just now';
    if (diff < 60) return `${diff}m ago`;
    return `${Math.floor(diff / 60)}h ${diff % 60}m ago`;
  };

  // Count unique tenants in active sessions
  const uniqueTenants = new Set(sessions.map(s => s.tenantId)).size;

  return (
    <div className="mt-6">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded shadow-lg text-white text-sm
          ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
          {toast.message}
        </div>
      )}

      {/* Tab header */}
      <div className="flex gap-2 mb-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('sessions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors
            ${activeTab === 'sessions'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Active Sessions
          {sessions.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-teal-100 
                             text-teal-700 rounded-full">
              {sessions.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('locks')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors
            ${activeTab === 'locks'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'}`}
        >
          Record Locks
          {locks.length > 0 && (
            <span className="ml-2 px-2 py-0.5 text-xs bg-amber-100 
                             text-amber-700 rounded-full">
              {locks.length}
            </span>
          )}
        </button>
      </div>

      {/* SESSIONS TAB */}
      {activeTab === 'sessions' && (
        <div>
          {/* Summary + Refresh */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-gray-800">
                {sessions.length}
              </span> users active across{' '}
              <span className="font-semibold text-gray-800">
                {uniqueTenants}
              </span> tenants
            </p>
            <button
              onClick={fetchSessions}
              disabled={sessionsLoading}
              className="px-3 py-1.5 text-xs bg-white border border-gray-300 
                         rounded hover:bg-gray-50 text-gray-600 
                         disabled:opacity-50"
            >
              {sessionsLoading ? 'Refreshing...' : '↺ Refresh'}
            </button>
          </div>

          {/* Sessions table */}
          {sessionsLoading && sessions.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              Loading sessions...
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm 
                            border border-dashed border-gray-200 rounded">
              No active sessions
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 
                                  text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">User</th>
                    <th className="px-4 py-3 text-left">Role</th>
                    <th className="px-4 py-3 text-left">Tenant</th>
                    <th className="px-4 py-3 text-left">Last Active</th>
                    <th className="px-4 py-3 text-left">IP Address</th>
                    <th className="px-4 py-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sessions.map((session) => (
                    <tr key={session.sessionId} 
                        className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {session.userName}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 text-xs rounded-full 
                                         bg-blue-100 text-blue-700">
                          {session.userRole}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs font-mono">
                        {session.tenantId?.toString().slice(-8) || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {formatRelative(session.lastActiveAt)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs font-mono">
                        {session.ipAddress || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleRevokeSession(
                            session.sessionId, 
                            session.userName
                          )}
                          className="px-3 py-1 text-xs bg-red-50 text-red-600 
                                     border border-red-200 rounded 
                                     hover:bg-red-100 transition-colors"
                        >
                          Force Logout
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* LOCKS TAB */}
      {activeTab === 'locks' && (
        <div>
          {/* Header + Refresh */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-600">
              {lastUpdated && (
                <span>Last updated: {formatRelative(lastUpdated)}</span>
              )}
            </p>
            <button
              onClick={fetchLocks}
              disabled={locksLoading}
              className="px-3 py-1.5 text-xs bg-white border border-gray-300 
                         rounded hover:bg-gray-50 text-gray-600 
                         disabled:opacity-50"
            >
              {locksLoading ? 'Refreshing...' : '↺ Refresh'}
            </button>
          </div>

          {/* Locks table */}
          {locksLoading && locks.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              Loading locks...
            </div>
          ) : locks.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm 
                            border border-dashed border-gray-200 rounded">
              No active record locks
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 
                                  text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left">Record ID</th>
                    <th className="px-4 py-3 text-left">Type</th>
                    <th className="px-4 py-3 text-left">Locked By</th>
                    <th className="px-4 py-3 text-left">Locked Since</th>
                    <th className="px-4 py-3 text-left">Expires</th>
                    <th className="px-4 py-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {locks.map((lock) => (
                    <tr key={lock.lockId} 
                        className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-xs font-mono text-gray-600">
                        {lock.recordId?.slice(-8) || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 text-xs rounded-full 
                                         bg-purple-100 text-purple-700">
                          {lock.recordType}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {lock.lockedByName}
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {formatRelative(lock.lockedAt)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {formatTime(lock.expiresAt)}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleReleaseLock(
                            lock.lockId,
                            lock.lockedByName
                          )}
                          className="px-3 py-1 text-xs bg-amber-50 
                                     text-amber-600 border border-amber-200 
                                     rounded hover:bg-amber-100 
                                     transition-colors"
                        >
                          Force Unlock
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SuperAdminSessionManagement;
